import React from 'react';
import {Store} from '../../models/store.model';
import {FileUploadField} from './FileUploadField';

interface StoreAdditionalFieldsProps {
  formData: Partial<Store>;
  onInputChange: (field: keyof Store, value: string) => void;
  onFileChange: (field: keyof Store, file: File | File[] | null) => void;
  onRemoveExistingImage: (imageType: 'commercialRegisterImage' | 'taxCardImage' | 'profileImage' | 'storeImages', imageUrl: string) => void;
  removedImages: {
    commercialRegisterImage?: string;
    taxCardImage?: string;
    profileImage?: string;
    storeImages?: string[];
  };
}

export const StoreAdditionalFields: React.FC<StoreAdditionalFieldsProps> = ({
  formData,
  onInputChange,
  onFileChange,
  onRemoveExistingImage,
  removedImages
}) => {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">معلومات إضافية</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Notes */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            ملاحظات
          </label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => onInputChange('notes', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
            placeholder="أدخل أي ملاحظات إضافية"
            rows={3}
          />
        </div>

        {/* Commercial Register Section */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-800">السجل التجاري</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              رقم السجل التجاري
            </label>
            <input
              type="text"
              value={formData.commercialRegisterNumber || ''}
              onChange={(e) => onInputChange('commercialRegisterNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
              placeholder="أدخل رقم السجل التجاري"
              dir="ltr"
            />
          </div>

          <FileUploadField
            label="صورة السجل التجاري"
            value={formData.commercialRegisterImageFile}
            onChange={(file) => onFileChange('commercialRegisterImageFile', file)}
            accept="image/*"
            maxSizeInMB={5}
            existingImages={formData.commercialRegisterImage ? [formData.commercialRegisterImage] : []}
            onRemoveExistingImage={(imageUrl) => onRemoveExistingImage('commercialRegisterImage', imageUrl)}
          />
        </div>

        {/* Tax Card Section */}
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-800">البطاقة الضريبية</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              رقم البطاقة الضريبية
            </label>
            <input
              type="text"
              value={formData.taxCardNumber || ''}
              onChange={(e) => onInputChange('taxCardNumber', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-brand-500"
              placeholder="أدخل رقم البطاقة الضريبية"
              dir="ltr"
            />
          </div>

          <FileUploadField
            label="صورة البطاقة الضريبية"
            value={formData.taxCardImageFile}
            onChange={(file) => onFileChange('taxCardImageFile', file)}
            accept="image/*"
            maxSizeInMB={5}
            existingImages={formData.taxCardImage ? [formData.taxCardImage] : []}
            onRemoveExistingImage={(imageUrl) => onRemoveExistingImage('taxCardImage', imageUrl)}
          />
        </div>

        {/* Profile Image */}
        <div className="md:col-span-2">
          <FileUploadField
            label="صورة الملف الشخصي للمتجر"
            value={formData.profileImageFile}
            onChange={(file) => onFileChange('profileImageFile', file)}
            accept="image/*"
            maxSizeInMB={5}
            existingImages={formData.profileImageUrl ? [formData.profileImageUrl] : []}
            onRemoveExistingImage={(imageUrl) => onRemoveExistingImage('profileImage', imageUrl)}
          />
        </div>

        {/* Store Images */}
        <div className="md:col-span-2">
          <FileUploadField
            label="صور المتجر"
            value={formData.storeImagesFiles}
            onChange={(file) => onFileChange('storeImagesFiles', file)}
            accept="image/*"
            multiple={true}
            maxFiles={10}
            maxSizeInMB={5}
            existingImages={formData.images ? formData.images.filter(img => !removedImages.storeImages?.includes(img)) : []}
            onRemoveExistingImage={(imageUrl) => onRemoveExistingImage('storeImages', imageUrl)}
          />
        </div>
      </div>
    </div>
  );
};
